/**
 * Subscription interval options for billing frequency
 */
export const SubscriptionIntervals = {
  MONTHLY: 'MONTHLY',
  YEARLY: 'YEARLY',
} as const;

/**
 * Subscription block status values
 */
export const SubscriptionStatuses = {
  AVAILABLE: 'AVAILABLE',
  UNAVAILABLE: 'UNAVAILABLE',
  PAYMENT_REQUIRED: 'PAYMENT_REQUIRED',
  IN_PROGRESS: 'IN_PROGRESS',
} as const;

/**
 * Subscription block payment sub-status values.
 * Reflects whether the current billing cycle has been collected.
 */
export const SubscriptionBlockSubStatuses = {
  PRE_PAID: 'PRE_PAID',
  PAID: 'PAID',
  PENDING: 'PENDING',
  NONE: 'NONE',
} as const;

/**
 * Subscription block customer state values.
 * `INACTIVE` marks a churned store: the block is read-only and most actions are disabled.
 */
export const CustomerStates = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

/**
 * Payment provider identifiers
 */
export const PaymentProviders = {
  STRIPE: 'STRIPE',
  FINTOC: 'FINTOC',
} as const;

/**
 * Payment method type identifiers (card vs SPEI bank transfer)
 */
export const PaymentMethodTypes = {
  CARD: 'CARD',
  SPEI: 'SPEI',
} as const;

/**
 * Stripe checkout session modes. The front picks the mode from the block's
 * license status: `AVAILABLE` → register a new card and run the $10 hold
 * (`NEW_PAYMENT_METHOD_AND_CHARGE`); otherwise collect the overdue subscription
 * (`PAY_SUBSCRIPTION`).
 */
export const PaymentSessionModes = {
  NEW_PAYMENT_METHOD_AND_CHARGE: 'NEW_PAYMENT_METHOD_AND_CHARGE',
  PAY_SUBSCRIPTION: 'PAY_SUBSCRIPTION',
  /** Reactivation charge: the remainder of the cycle for the plan and its add-ons, plus the one-off fee. */
  REACTIVATION: 'REACTIVATION',
} as const;

/** Union of the supported Stripe checkout session modes. */
export type PaymentSessionMode = typeof PaymentSessionModes[keyof typeof PaymentSessionModes];

/**
 * Card brand identifiers for payment methods
 */
export const CardBrands = {
  VISA: 'VISA',
  MASTERCARD: 'MASTERCARD',
  AMEX: 'AMEX',
} as const;

/**
 * CFDI usage SAT codes. Values match the catalog returned by the backend
 * (P01 = por definir, G03 = gastos en general, G01 = adquisición de mercancías,
 * S01 = sin efectos fiscales).
 */
export const CfdiUses = {
  UNDEFINED: 'P01',
  GENERAL_EXPENSES: 'G03',
  MERCHANDISE_ACQUISITION: 'G01',
  NO_TAX_EFFECTS: 'S01',
} as const;

/**
 * Maps each SAT fiscal regime code (c_RegimenFiscal) to the CFDI usage
 * (c_UsoCFDI) that must be pre-selected when that regime is chosen.
 *
 * Source of truth (SAT catalog + product rules):
 * - Gastos en general (G03): 601, 603, 605, 606, 612, 620, 621, 622, 623, 624, 625, 626
 * - Sin efectos fiscales (S01): 607, 608, 610, 611, 614, 615, 616
 *
 * Note: some codes apply to both persona física and persona moral — 610
 * (Residentes en el Extranjero) maps to S01 in both, and 626 (Régimen
 * Simplificado de Confianza) maps to G03 in both. Since each shared code resolves
 * to the same CFDI regardless of person type, the lookup stays keyed purely by
 * SAT code.
 */
export const CfdiUseByTaxRegime = {
  '601': CfdiUses.GENERAL_EXPENSES,
  '603': CfdiUses.GENERAL_EXPENSES,
  '605': CfdiUses.GENERAL_EXPENSES,
  '606': CfdiUses.GENERAL_EXPENSES,
  '607': CfdiUses.NO_TAX_EFFECTS,
  '608': CfdiUses.NO_TAX_EFFECTS,
  '610': CfdiUses.NO_TAX_EFFECTS,
  '611': CfdiUses.NO_TAX_EFFECTS,
  '612': CfdiUses.GENERAL_EXPENSES,
  '614': CfdiUses.NO_TAX_EFFECTS,
  '615': CfdiUses.NO_TAX_EFFECTS,
  '616': CfdiUses.NO_TAX_EFFECTS,
  '620': CfdiUses.GENERAL_EXPENSES,
  '621': CfdiUses.GENERAL_EXPENSES,
  '622': CfdiUses.GENERAL_EXPENSES,
  '623': CfdiUses.GENERAL_EXPENSES,
  '624': CfdiUses.GENERAL_EXPENSES,
  '625': CfdiUses.GENERAL_EXPENSES,
  '626': CfdiUses.GENERAL_EXPENSES,
} as const;

/**
 * Invoicing outcome the backend reports per CFDI, in `invoiceData.status` and in each
 * `relatedInvoices[].status`. Describes the *invoice*, not the charge: a settled payment can
 * carry a FAILED invoice, which is what the re-invoicing flow exists for.
 */
export const InvoiceStatuses = {
  ISSUED: 'ISSUED',
  FAILED: 'FAILED',
} as const;

/**
 * Status of the charge a subscription owes. It is not an API value: the payments
 * endpoint never reports it, because a transfer that never arrived leaves no payment behind.
 */
export const OVERDUE_CHARGE_STATUS = 'OVERDUE';

/** Marca que queda en lugar de un RFC redactado (Charter §5.1) */
export const REDACTED_RFC = '[RFC]';
